"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import { useAuth } from "@/components/auth-provider";
import { isGuestUser } from "@/lib/guest-mode";
import { getMySubscription } from "@/lib/supabase-client";
import {
  isGrandfathered,
  resolvePremiumStatus,
  type PremiumStatus,
} from "@/lib/premium";

// クラウド同期 (Premium) の判定状態。mobile/src/contexts/PremiumContext.tsx の移植
// (Web に RevenueCat は無いので hasActiveEntitlement は常に false)。
// - ゲスト (匿名) / 未認証は常に isPremium=false, isResolving=false (判定対象外)
// - グランドファザーは user.created_at だけで即確定 (ネットワーク待ちなし)
// - それ以外は subscriptions テーブルで確定。premium の確定結果は localStorage に
//   キャッシュし、オフライン起動 (特に Tauri) で購入済みユーザーを
//   ペイウォールへ誤誘導しない

interface PremiumContextType extends PremiumStatus {
  // 認証済みで初回判定がまだ確定していない間だけ true
  isResolving: boolean;
  refresh: () => Promise<void>;
}

const NOT_PREMIUM: PremiumStatus = { isPremium: false, reason: "none" };

const PremiumContext = createContext<PremiumContextType | undefined>(undefined);

const cacheKey = (userId: string) => `premium_status_${userId}`;

export function PremiumProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<PremiumStatus>(NOT_PREMIUM);
  const [isFetching, setIsFetching] = useState(false);
  // このユーザー id について判定が一度でも確定したか。effect (resolve) は
  // paint 後に走るため、これ無しだと「commit 直後・effect 前」の1フレームが
  // isResolving=false になり、app-page のゲートがペイウォールを点滅させる
  const [resolvedForId, setResolvedForId] = useState<string | null>(null);
  // ユーザー切り替え中に古い非同期結果が新しい状態を上書きしないための世代番号
  const generation = useRef(0);

  const resolve = useCallback(async (u: User) => {
    const gen = ++generation.current;

    if (isGrandfathered(u.created_at)) {
      setStatus({ isPremium: true, reason: "grandfathered" });
      setResolvedForId(u.id);
      setIsFetching(false);
      return;
    }

    setIsFetching(true);

    // 確定前にキャッシュを反映 (premium だった場合のみ)。
    // オフラインでは下のフェッチが失敗し「未購入」に見えてしまうため
    try {
      const cached = localStorage.getItem(cacheKey(u.id));
      if (cached && generation.current === gen) {
        const parsed = JSON.parse(cached) as PremiumStatus;
        if (parsed?.isPremium) {
          setStatus(parsed);
          setResolvedForId(u.id);
          setIsFetching(false);
        }
      }
    } catch {
      // キャッシュ破損は無視して通常判定へ
    }

    const result = await getMySubscription();
    if (generation.current !== gen) return;

    if (!result.ok) {
      // ネットワーク失敗: 判定を確定させない。キャッシュ由来の premium 表示は
      // 維持し、none での上書きもキャッシュへの書き込みもしない
      // (書くと1回のオフライン起動で購入済みユーザーのキャッシュが毒される)
      setResolvedForId(u.id);
      setIsFetching(false);
      return;
    }

    const resolved = resolvePremiumStatus({
      userCreatedAt: u.created_at,
      subscription: result.subscription,
      hasActiveEntitlement: false,
    });
    setStatus(resolved);
    setResolvedForId(u.id);
    setIsFetching(false);
    try {
      localStorage.setItem(cacheKey(u.id), JSON.stringify(resolved));
    } catch {
      // ストレージ満杯等は無視 (次回はネットワーク判定に頼る)
    }
  }, []);

  useEffect(() => {
    if (!user || isGuestUser(user)) {
      generation.current++;
      setStatus(NOT_PREMIUM);
      setResolvedForId(null);
      setIsFetching(false);
      return;
    }
    void resolve(user);
    // user オブジェクトはトークン更新のたびに差し替わるため id だけを見る。
    // is_anonymous はゲスト昇格 (updateUser 方式 = id 不変) の再判定に必要
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.is_anonymous, resolve]);

  // 非ゲスト認証済みで判定未確定 (別ユーザーの確定しか無い場合を含む) は resolving
  const isResolving =
    isFetching ||
    (!!user && !isGuestUser(user) && resolvedForId !== user.id);

  const refresh = useCallback(async () => {
    if (user && !isGuestUser(user)) await resolve(user);
  }, [user, resolve]);

  return (
    <PremiumContext.Provider value={{ ...status, isResolving, refresh }}>
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  const context = useContext(PremiumContext);
  if (context === undefined) {
    throw new Error("usePremium must be used within a PremiumProvider");
  }
  return context;
}
